/**
 * Splits an array into smaller chunks of a specified size.
 *
 * @param items - The array to split into chunks
 * @param chunkSize - The size of each chunk
 * @returns An array of arrays, where each inner array is a chunk of the original array
 *
 * For example:
 * chunkItems([1,2,3,4,5], 2) returns [[1,2], [3,4], [5]]
 *
 * If chunkSize is 0, returns the original array wrapped in an outer array.
 * Otherwise, divides the array into chunks of the specified size, with the last chunk
 * potentially being smaller if the array length is not evenly divisible by chunkSize.
 */
export function chunkItems<T>(items: T[], chunkSize: number): T[][] {
    if (chunkSize === 0) {
        return [items];
    }
    return Array.from(
        { length: Math.ceil(items.length / chunkSize) },
        (_, index) =>
            items.slice(index * chunkSize, index * chunkSize + chunkSize)
    );
}

/**
 * Injects items into specific positions in an array.
 *
 * @param items - The original array to inject items into
 * @param injectables - Array of items to inject
 * @param positions - Array of positions where items should be injected
 * @returns The modified array with injected items
 *
 * For example:
 * injectItemIntoChunk([1,2,3], [10,20], [1,2]) returns [1,10,2,20,3]
 *
 * The function:
 * 1. Checks that injectables and positions arrays have same length
 * 2. Sorts positions in descending order to avoid position shifts
 * 3. Injects each item at its specified position if:
 *    - The item exists (not null/undefined)
 *    - Position is valid (>= 0 and <= array length)
 * 4. Returns the modified array
 *
 * Used to insert ads and featured projects ("potms") at specific positions
 * in the project tiles grid.
 */
export function injectItemIntoChunk<T>(
    items: T[],
    injectables: T[],
    positions: number[]
): T[] {
    if (injectables.length === positions.length) {
        // Sort positions and injectables based on positions in descending order
        let sortedIndices = positions
            .map((pos, index) => ({ pos, value: injectables[index] }))
            .sort((a, b) => b.pos - a.pos);

        // Inject values at the corresponding positions
        sortedIndices.forEach((item) => {
            let { pos, value } = item;
            if (value && pos >= 0 && pos <= items.length) {
                items.splice(pos, 0, value);
            }
        });
    }

    return items;
}
